/**
 * AI Workflow Node Approval Modal
 *
 * Simplified approval UI:
 * - Always shows Approve/Cancel buttons
 * - Optional textarea for additional context/notes
 * - Optional file upload for supporting documents
 * - No required inputs - user can approve immediately
 *
 * Integrates with useAIWorkflowApproval hook for backend submission.
 */

import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import {
  X,
  CheckCircle,
  AlertCircle,
  User,
  FileText,
  Upload,
  Loader
} from 'lucide-react';

/**
 * Modal component for approving workflow nodes
 */
export default function AIWorkflowNodeApprovalModal({
  node,
  isOpen,
  onClose,
  onApprove,
  workflowId
}) {
  const [textInput, setTextInput] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setTextInput('');
      setUploadedFiles([]);
      setError(null);
      setUploadError(null);
    }
  }, [isOpen, node]);

  if (!isOpen || !node) return null;

  const nodeData = node.data || {};
  const approvalPrompt = nodeData.approval_prompt || nodeData.approvalPrompt || 'Please review and approve this step.';
  const displayName = nodeData.displayName || nodeData.display_name || 'Node';
  const description = nodeData.description || '';

  // Handle file upload
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadError(null);

    try {
      // Upload each file to fileserver
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post('/api/v1/files/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        return {
          id: response.data.file_id,
          name: file.name,
          size: file.size,
          type: file.type,
          download_url: response.data.download_url
        };
      });

      const uploadedFileData = await Promise.all(uploadPromises);
      setUploadedFiles(prev => [...prev, ...uploadedFileData]);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to upload files';
      setUploadError(errorMessage);
      console.error('File upload error:', err);
    }
  };

  // Remove uploaded file
  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Handle approve
  const handleApprove = async () => {
    setSubmitting(true);
    setError(null);

    try {
      // No validation - user can approve immediately
      const approvalData = {
        approved: true,
        decision_notes: textInput.trim() || "Approved without additional context",
        files: uploadedFiles.length > 0 ? uploadedFiles.map(f => f.id) : undefined
      };

      await onApprove(node.id, approvalData);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to approve node');
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        // Close modal only if clicking backdrop (not content)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-6 py-4 border-b border-gray-200 flex items-center justify-between"
          style={{ borderTopColor: '#EAB308', borderTopWidth: '4px' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: '#EAB30820' }}
            >
              <User size={24} style={{ color: '#EAB308' }} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Approval Required
              </h2>
              <p className="text-sm text-gray-600">
                {displayName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content - scrollable area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Error message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {/* Description */}
          {description && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <FileText size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">{description}</div>
              </div>
            </div>
          )}

          {/* Approval Prompt */}
          <div className="mb-6">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-yellow-900 mb-1">Review Required</div>
                  <div className="text-sm text-yellow-800">
                    <ReactMarkdown>{approvalPrompt}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Always show optional context textarea */}
          <div className="mb-6">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Additional Context (Optional)
            </label>
            <textarea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Add any notes or context for this approval..."
              className="w-full h-24 px-4 py-3 border border-gray-300 rounded-lg resize-none text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional: Add context or notes about your approval decision.
            </p>
          </div>

          {/* File upload section - always visible, always optional */}
          <div className="mb-6">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Upload size={16} />
              Attach Files (Optional)
            </label>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <Upload size={32} className="text-gray-400" />
                <div className="text-sm text-gray-600">
                  Click to upload or drag and drop
                </div>
                <div className="text-xs text-gray-500">
                  Optional: Attach relevant files
                </div>
              </label>
            </div>

            {/* Upload error */}
            {uploadError && (
              <div className="mt-2 text-sm text-red-600">{uploadError}</div>
            )}

            {/* Uploaded files list */}
            {uploadedFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {uploadedFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-gray-600" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{file.name}</div>
                        <div className="text-xs text-gray-500">
                          {(file.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(file.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-xs text-gray-500">
            Node: {node.data?.nodeKey || node.data?.node_key || node.id}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <X size={16} />
              <span>Cancel</span>
            </button>
            <button
              onClick={handleApprove}
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  <span>Approving...</span>
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  <span>Approve</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
